import { css } from "@emotion/css";
import { Button, ExternalLink, LinearProgress, resetStyles, formatExternalUrl } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { nameForLink, useDispatchLinkClicked } from "@octopusdeploy/portal-routes";
import type React from "react";
import { OctopusRoundTable } from "./OctopusRoundTable";
interface SpaceCallToActionPanelProps {
    onNewSpaceRequested: () => void;
    isLoading: boolean;
    errorAlert?: React.ReactNode;
    isWithinLicenseLimit: boolean;
    licenseSerialNumber?: string;
}
export function SpaceCallToActionPanel({ onNewSpaceRequested, isLoading, errorAlert, isWithinLicenseLimit, licenseSerialNumber }: SpaceCallToActionPanelProps) {
    const upgradeLinkLabel = "Upgrade";
    const upgradePlanLink = licenseSerialNumber ? formatExternalUrl(`plan-upgrade?serial=${licenseSerialNumber}`) : formatExternalUrl("ControlCenter");
    const dispatchLinkClicked = useDispatchLinkClicked();
    return (<div className={spaceCallToActionPanelStyles}>
            {errorAlert && <div className={headerStyles}>{errorAlert}</div>}
            <>
                <div className={contentStyles}>
                    <div className={imageStyles}>
                        <OctopusRoundTable />
                    </div>
                    <h3 className={headingStyles}>Give teams full control with dedicated spaces</h3>
                    {!isWithinLicenseLimit ? (<>
                            <p className={copyStyles}>The Free tier includes one space to get you started. Dedicated spaces let each team manage their own projects, deployments, and configurations without conflicts.</p>
                            <p className={copyStyles}>Upgrade to access unlimited spaces.</p>
                        </>) : (<p className={copyStyles}>Dedicated spaces let each team manage their own projects, deployments, and configurations without conflicts.</p>)}
                    <div />
                    <div className={upgradePlanStyles}>
                        {isWithinLicenseLimit ? (<Button label={"Add Space"} importance="primary" onClick={onNewSpaceRequested}/>) : (<Button label={upgradeLinkLabel} importance="loud" href={upgradePlanLink} external={true} onClick={() => dispatchLinkClicked(nameForLink(), {
                linkLabel: upgradeLinkLabel,
                documentUrl: upgradePlanLink,
            })}/>)}
                        <ExternalLink href="spaces">Docs: Spaces</ExternalLink>
                    </div>
                </div>
                <LinearProgress variant={"indeterminate"} show={isLoading}/>
            </>
        </div>);
}
const spaceCallToActionPanelStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minHeight: "33.75rem",
});
const headerStyles = css({
    padding: space[16],
});
const contentStyles = css({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: space[16],
    padding: `${space[16]} ${space[32]}`,
});
const imageStyles = css({
    width: "200px",
    height: "200px",
});
const headingStyles = css({
    ...resetStyles.heading,
    color: themeTokens.color.text.primary,
    font: text.heading.medium,
    textAlign: "center",
});
const copyStyles = css({
    ...resetStyles.paragraph,
    font: text.body.regular.large,
    color: themeTokens.color.text.secondary,
    textAlign: "center",
    maxWidth: "24rem",
});
const upgradePlanStyles = css({
    display: "flex",
    gap: space[16],
    alignItems: "center",
});
